import Head from 'next/head';
import { useEffect, useState } from 'react';

import { getSEOConfig, structuredData } from '../utils/seo';
import { getMarketingNavData } from '../queries/marketingNav';

import Empty from '../layouts/empty';
import { Navigation } from '../components/landing/v2/Navigation';
import { ProgressBar } from '../components/landing/v2/ProgressBar';
import { Footer } from '../components/landing/v2/Footer';

import { QuoteBuilderHero } from '../components/landing/v2/quote-builder/QuoteBuilderHero';
import {
  BuiltForCustomWork,
  TheSameCustomerTwice,
  InlineCtaAfterContrast,
  HowItWorks,
  InlineCtaAfterHowItWorks,
  WhyItsWorthIt,
  KeepYourProcess,
  WhatsIncluded,
  QuoteBuilderCta,
  UpgradePath,
} from '../components/landing/v2/quote-builder/QuoteBuilderSections';

// /quote-builder describes the inquiry workflow and invites a demo. Existing
// account limits and signup behavior remain in the application. Screenshots
// show quote requests, rather than checkout or product prices.

export default function QuoteBuilderPage({ navData }) {
  // Marketing surface is pinned to dark mode (toggle removed from public
  // pages — see _app.js).
  const isDarkMode = true;
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const seoConfig = getSEOConfig('quoteBuilder');

  useEffect(() => {
    const handleScroll = () => {
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(maxScroll > 0 ? window.scrollY / maxScroll : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Head>
        <title>{seoConfig.title}</title>
        <meta name='title' content={seoConfig.title} />
        <meta name='description' content={seoConfig.description} />
        <meta name='keywords' content={seoConfig.keywords?.join(', ') || ''} />
        <meta name='author' content={seoConfig.author} />
        <meta name='robots' content={seoConfig.robots} />

        <link rel='canonical' href={seoConfig.canonical} />

        <meta property='og:type' content={seoConfig.ogType} />
        <meta property='og:url' content={seoConfig.ogUrl} />
        <meta property='og:title' content={seoConfig.ogTitle} />
        <meta property='og:description' content={seoConfig.ogDescription} />
        <meta property='og:image' content={seoConfig.ogImage} />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='630' />
        <meta property='og:site_name' content={seoConfig.siteName} />
        <meta property='og:locale' content='en_US' />

        <meta property='twitter:card' content='summary_large_image' />
        <meta property='twitter:url' content={seoConfig.ogUrl} />
        <meta property='twitter:title' content={seoConfig.ogTitle} />
        <meta
          property='twitter:description'
          content={seoConfig.ogDescription}
        />
        <meta property='twitter:image' content={seoConfig.ogImage} />

        <meta name='theme-color' content='#006B45' />

        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              structuredData.breadcrumbList([
                { name: 'Home', url: 'https://timbercloud.com' },
                {
                  name: 'Quote Builder',
                  url: 'https://timbercloud.com/quote-builder',
                },
              ]),
            ),
          }}
        />
      </Head>

      <Navigation
        isDarkMode={isDarkMode}
        isNavOpen={isNavOpen}
        setIsNavOpen={setIsNavOpen}
        navData={navData}
      />

      <ProgressBar scrollProgress={scrollProgress} isDarkMode={isDarkMode} />

      <main className='min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950 text-white'>
        <QuoteBuilderHero />
        <BuiltForCustomWork />
        <TheSameCustomerTwice />
        <InlineCtaAfterContrast />
        <HowItWorks />
        <InlineCtaAfterHowItWorks />
        <WhyItsWorthIt />
        <KeepYourProcess />
        <WhatsIncluded />
        <QuoteBuilderCta />
        <UpgradePath />
      </main>

      <Footer isDarkMode={isDarkMode} navData={navData} />
    </>
  );
}

QuoteBuilderPage.getLayout = (page) => <Empty>{page}</Empty>;

// Opt out of _app.js's dashboard hydration shield — this page must
// server-render its body + <Head> for crawlers and link scrapers.
QuoteBuilderPage.ssrEnabled = true;

export async function getStaticProps() {
  const navData = await getMarketingNavData();
  return { props: { navData }, revalidate: 300 };
}
